import Vue from 'vue'

import { cloneDeep, debounce, isEqual } from 'lodash'

import { Services, joinURLPath } from '@sigma-frontend-libs/commons'
import { permission as SCHEMA } from '@sigma-legacy-libs/essentials/lib/schemas'

import { globalErrorHandler, permissionAccepts, request } from '@/utils'

import render from './render'

/**
 * Проверяет, содержит ли строка value подстроку search (без учёта регистра).
 * @param {string} value - строка, в которой ищем
 * @param {string} search - подстрока для поиска
 * @returns {boolean}
 */
const search = (value = '', search = '') => {
  return value.toLowerCase().includes(search.toLowerCase())
}

const acceptsAdvanced = [
  permissionAccepts.false,
  permissionAccepts.me,
  permissionAccepts.reseller,
  permissionAccepts.manager,
  permissionAccepts.true
]

const acceptsGranular = [
  permissionAccepts.default,
  permissionAccepts.read,
  permissionAccepts.write,
  permissionAccepts.false
]

export default {
  name: Services.permissions,

  props: {
    UserId: {
      type: String,
      default: undefined
    }
  },

  data() {
    return {
      loading: {
        find: false,
        update: {},
        preset: {}
      },

      items: undefined,
      search: '',

      currentTab: 'advanced'
    }
  },

  computed: {
    $_items() {
      const result = {}
      const pathToTranslate = `${Services.permissions}.services`

      if (!this.items) {
        return {}
      }

      let schema = SCHEMA.RESELLER_PERMISSIONS_SCHEMA
      if (this.checkPermissions(`advanced.${Services.users}.setPermissions`)) {
        schema = SCHEMA.PERMISSIONS_SCHEMA
      }

      for (const key in this.items) {
        let [ root, service, action, sub ] = key.split('.')

        if (root) {
          if (!result[root]) {
            result[root] = {}
          }
          switch (root) {
            case 'advanced': {
              if (service && action) {
                if (sub) {
                  service = [ service, action ].join('.')
                  action = sub
                }
                const title = this.getTranslate(`${pathToTranslate}.${service}`)
                const includeSearch = search(service, this.search) || search(title, this.search)
                if (includeSearch) {
                  if (!result[root][service]) {
                    result[root][service] = []
                  }

                  const schemaOptions = schema?.[key]?.$options?.map(({ $value }) => {
                    return $value
                  })
                  let options = acceptsAdvanced
                  if (Array.isArray(schemaOptions) && schemaOptions.length) {
                    options = schemaOptions
                  }
                  result[root][service].push({
                    key,
                    action,
                    options
                  })
                }
              }
              break
            }
            case 'secret': {
              if (this.checkPermissions('secret.showSecrets')) {
                const title = this.getTranslate(`${pathToTranslate}.${root}`)
                const includeSearch = search(root, this.search) || search(title, this.search)
                if (includeSearch) {
                  if (service) {
                    if (!result[root][root]) {
                      result[root][root] = []
                    }

                    const schemaOptions = schema?.[key]?.$options?.map(({ $value }) => {
                      return $value
                    })
                    let options = acceptsAdvanced
                    if (Array.isArray(schemaOptions) && schemaOptions.length) {
                      options = schemaOptions
                    }
                    result[root][root].push({
                      key,
                      action: service,
                      options
                    })
                  }
                }
              }
              break
            }
            case 'granular': {
              const [ leftPartKey, settingPath ] = key.split(':')
              const leftPartSlice = leftPartKey.split('.').slice(1)
              service = leftPartSlice.join('.')
              const actionFull = [ service, settingPath ].join('.')
              const titleService = this.getTranslate(`${pathToTranslate}.${service}`)
              const titleAction = this.getTranslate(`${Services.permissions}.labels.${actionFull}`)
              const titleSetting = this.getTranslate(`${Services.permissions}.labels.${settingPath}`)

              const includeSearch =
                search(root, this.search) ||
                search(service, this.search) ||
                search(settingPath, this.search) ||
                search(actionFull, this.search) ||
                search(titleService, this.search) ||
                search(titleAction, this.search) ||
                search(titleSetting, this.search)

              if (includeSearch) {
                if (service) {
                  if (!result[root][service]) {
                    result[root][service] = []
                  }

                  const schemaOptions = schema?.[key]?.$options?.map(({ $value }) => {
                    return $value
                  })
                  let options = acceptsGranular
                  if (Array.isArray(schemaOptions) && schemaOptions.length) {
                    options = schemaOptions
                  }
                  result[root][service].push({
                    key,
                    action: actionFull,
                    options
                  })
                }
              }
              break
            }
          }
        }
      }

      const sortedResult = {}
      for (const category of Object.keys(result)) {
        const keys = result[category]
        const sortedKeys = {}
        Object.keys(keys)
          .sort((a, b) => {
            const aTitle = this.getTranslate(`${Services.permissions}.services.${a}`)
            const bTitle = this.getTranslate(`${Services.permissions}.services.${b}`)

            return aTitle.localeCompare(bTitle)
          })
          .forEach(key => {
            return sortedKeys[key] = keys[key]
          })
        sortedResult[category] = sortedKeys
      }

      return sortedResult
    },

    tabs() {
      const result = [ 'advanced', 'granular', 'secret' ]
      const keys = Object.keys(this.$_items)
      const hasTab = tab => {
        if (!keys.includes(tab)) {
          return false
        }
        if (tab === 'secret') {
          return this.checkPermissions('secret.showSecrets')
        }

        return true
      }

      return result.filter(hasTab)
    },

    permissionAccepts() {
      switch (this.currentTab) {
        case 'granular': return acceptsGranular
        case 'advanced':
        case 'secret':
        default: return acceptsAdvanced
      }
    }
  },

  mounted() {
    this.getPermissions()
  },

  methods: {
    async getPermissions() {
      try {
        this.loading.find = true
        const data = await request.find(joinURLPath(Services.permissions, this.UserId))
        if (data) {
          this.items = data
        }
      } catch (error) {
        globalErrorHandler(error)
      } finally {
        this.loading.find = false
      }
    },

    updatePermission: debounce(
      async function(field, value) {
        const previousValue = cloneDeep(this.items[field])
        if (!isEqual(previousValue, value)) {
          try {
            const data = await request.update(joinURLPath(Services.permissions, this.UserId, field), { body: { value } })
            if (data) {
              this.items[field] = data.value
              this.addSnackbar({
                type: 'success',
                text: this.getTranslate(`${Services.permissions}.snackbars.updated`)
              })
            }
          } catch (error) {
            globalErrorHandler(error)
          } finally {
            Vue.set(this.loading.update, `${field}:${value}`, false)
          }
        }
      },
      250,
      {
        leading: false,
        trailing: true
      }
    ),

    async setByPreset(preset) {
      if (this.$_items) {
        Vue.set(this.loading.preset, preset, true)
        for (const category in this.$_items) {
          for (const service in this.$_items?.[category]) {
            for (const { key, options } of this.$_items?.[category]?.[service]) {
              if (options?.find(option => {
                return option === preset
              })) {
                const data = await request.update(joinURLPath(Services.permissions, this.UserId, key), { body: { value: preset } })
                if (data) {
                  this.items[key] = data.value
                }
              }
            }
          }
        }
      }
    },

    getItems(tab) {
      return this.$_items?.[tab] || {}
    },
    getGranularItems(tab, header) {
      const items = this.getItems(tab)

      return items[header].reduce((result, item) => {
        const section = item.action.split('.')[2]
        if (!result[section]) {
          result[section] = []
        }
        result[section].push(item)

        return result
      }, {})
    }
  },

  render
}
